import { Point } from "@/lib/math/models/point"
import _ from 'lodash'
import { mathUtils } from "@/lib/math"
import { PolylineIndex } from "@/lib/geom";

export class ToothRegionBuilder {

    /**
     * 
     * @param {Point[]} toothPts 
     * @param {[Point, Point]} dividerSegment 
     */
    constructor(toothPts, dividerSegment) {
        /**
         * @type {Point[]}
         */
        this.toothPts = _.clone(toothPts);
        this.toothPolyline = new PolylineIndex(this.toothPts, { closed: true })
        /**
         * @type {[Point, Point]}
         */
        this.dividerSegment = Array.from(dividerSegment)
        // console.debug('ToothRegionBuilder.dividerSegment', dividerSegment)

        /**
         * @type {{dividerIndex: number, toothIndex: number, point: Point}[]}
         * @private
         */
        this.dividerIntersections = this._getDividerIntersections()
        // console.debug('ToothRegionBuilder.dividerIntersections', this.dividerIntersections)
    }

    /**
     * @param {'left' | 'right'} dividerSide
     * @returns {Point[]}
     */
    buildPoints(dividerSide) {
        const forwardPoints = this._slicePoints('forward');
        const backwardPoints = this._slicePoints('backward');

        // Faco uma verificacao para saber o lado certo da regiao.
        // console.debug('calculating forwardResult')
        const forwardResult = this._regionSideRelatedToDivider(forwardPoints)
        // console.debug('calculating backwardResult')
        const backwardResult = this._regionSideRelatedToDivider(backwardPoints)

        // Pego o menos ambiguo para ter mais certeza.
        const bestResult = [
            {
                ...forwardResult,
                sliceSide: 'forward'
            }, {
                ...backwardResult,
                sliceSide: 'backward'
            }
        ]
            .sort((a, b) => a.ambiguousRate - b.ambiguousRate)
            .at(0)

        // console.debug('forwardResult', forwardResult)
        // console.debug('backwardResult', backwardResult)
        // console.debug('bestResult', bestResult, 'dividerSide', dividerSide)

        if ((bestResult.sliceSide == 'forward' && bestResult.side == dividerSide)
            || (bestResult.sliceSide == 'backward' && bestResult.side != dividerSide))
            return forwardPoints;

        return backwardPoints;
    }

    _getDividerIntersections() {
        let extendedSegment = mathUtils.extendLineSegment(this.dividerSegment, 2, 'both')
        const intersections = this.toothPolyline.intersectSegment(extendedSegment.at(0), extendedSegment.at(1))
            .map((inter) => ({
                dividerIndex: inter.iA,
                toothIndex: inter.iB,
                point: inter.point
            }))
            .sort((interA, interB) => interA.toothIndex - interB.toothIndex)

        // console.debug('_getDividerIntersections', intersections)

        if (intersections.length <= 2)
            return intersections;

        // Deduplicate
        const uniqueIntersections = intersections
            .reduce((acc, current) => {
                // if (acc.some(item => current.toothIndex >= (item.toothIndex - 2) && current.toothIndex <= (item.toothIndex + 2)))
                //     return acc;
                if (!acc.some(item => item.point.x == current.point.x && item.point.y == current.point.y))
                    acc.push(current)
                return acc;
            }, [])

        // console.log('uniqueIntersections', uniqueIntersections)

        if (uniqueIntersections.length == 3) {
            const intersectionDividerMatches = uniqueIntersections.filter((inter) => {
                return this.dividerSegment
                    .some(dividerPoint => dividerPoint.x == inter.point.x && dividerPoint.y == inter.point.y)
            })

            // console.log('intersectionDividerMatches', intersectionDividerMatches)

            if (intersectionDividerMatches.length == 2)
                return intersectionDividerMatches
        }

        return uniqueIntersections
    }

    /**
     * @param {'forward' | 'backward'} side
     * @returns {Point[]}
     */
    _slicePoints(side) {
        if (this.dividerIntersections.length < 2)
            return [];

        const intersectionA = this.dividerIntersections.at(0)
        const intersectionB = this.dividerIntersections.at(1)
        if (this.dividerIntersections.length == 4) {
            const intersectionC = this.dividerIntersections.at(2)
            const intersectionD = this.dividerIntersections.at(3)
            switch (side) {
                case 'forward':
                    return [
                        intersectionA.point,
                        ...this.toothPts.slice(intersectionA.toothIndex + 1, intersectionB.toothIndex + 1),
                        intersectionB.point,

                        ...this.toothPts.slice(intersectionB.toothIndex + 1, intersectionC.toothIndex + 1),

                        intersectionC.point,
                        ...this.toothPts.slice(intersectionC.toothIndex + 1, intersectionD.toothIndex + 1),
                        intersectionD.point,

                        intersectionC.point,
                        ...this.toothPts.slice(intersectionB.toothIndex + 1, intersectionC.toothIndex + 1).reverse(),
                        intersectionB.point
                    ].filter(point => point)
                case 'backward':
                    return [
                        ...this.toothPts.slice(0, intersectionA.toothIndex + 1),
                        intersectionA.point,
                        intersectionB.point,

                        ...this.toothPts.slice(intersectionB.toothIndex + 1, intersectionC.toothIndex + 1),

                        intersectionC.point,
                        intersectionD.point,
                        ...this.toothPts.slice(intersectionD.toothIndex + 1)
                    ]
            }
        }

        var slicedPoints = [];
        switch (side) {
            case 'forward':
                slicedPoints = [
                    intersectionA.point,
                    ...this.toothPts.slice(intersectionA.toothIndex + 1, intersectionB.toothIndex + 1),
                    intersectionB.point
                ];
                break;
            case 'backward':
                slicedPoints = [
                    ...this.toothPts.slice(0, intersectionA.toothIndex + 1),
                    intersectionA.point,
                    intersectionB.point,
                    ...this.toothPts.slice(intersectionB.toothIndex + 1)
                ]
                break;
        }

        return slicedPoints
            .filter(point => point)
    }

    /**
     * Lado que a regiao esta relacionado ao segmento divider
     * 
     * @param {Point[]} regionPts 
     * @returns {{side: 'left' | 'right', ambiguousRate: number}}
     */
    _regionSideRelatedToDivider(regionPts) {
        const pointA = this.dividerSegment.at(0)
        const pointB = this.dividerSegment.at(1)
        const scoreBySide = regionPts.map(regionPoint => {
            // Inverto os eixos Y para garantir coerencia na hora que falo que é esquerda ou direita ao olhar para a imagem, 
            // devido ao plano cartesiano na imagem ter o Y invertido (origem 0,0 começando no left top).
            return mathUtils.pointSideRelatedToLineSegment(
                { x: regionPoint.x, y: -regionPoint.y },
                { x: pointA.x, y: -pointA.y },
                { x: pointB.x, y: -pointB.y }
            );
        }).reduce((acc, current) => {
            switch (current) {
                case 'left': acc.left++; break;
                case 'right': acc.right++; break;
                case 'center': acc.center++; break;
            }
            return acc;
        }, { left: 0, right: 0, center: 0 })

        const ambiguousRate = (Math.min(scoreBySide.right, scoreBySide.left) / Math.max(scoreBySide.right, scoreBySide.left))
        // console.debug('_regionSideRelatedToDivider.scoreBySide', scoreBySide, 'ambiguousRate', ambiguousRate)

        if (scoreBySide.left > scoreBySide.right)
            return { side: 'left', ambiguousRate }
        return { side: 'right', ambiguousRate };
    }

    // /**
    //  * @param {Point[]} regionPts 
    //  * @returns {boolean}
    //  */
    // _isRegionBeforeDivider(regionPts) {
    //     const pointA = this.dividerSegment.at(0)
    //     const pointB = this.dividerSegment.at(1)
    //     const beforeScore = regionPts.map(regionPoint => {
    //         return mathUtils.pointIsBeforeLineSegment(regionPoint, pointA, pointB) ? 1 : 0;
    //     }).reduce((acc, curr) => acc + curr, 0)

    //     return (beforeScore / regionPts.length) > 0.9;
    // }

}